import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { getDataAPI, putDataAPI } from '../../utils/fetchData';
import { setErrorAlert, setSuccessAlert } from '../../store/alert/alertSlice';
import LayoutHeader from '../../layout/LayoutHeader';

const TeamMemberEdit = () => {
  const { teamMemberId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const appColor = '#8b5cf6'; // Purple theme matching Teams app
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [teamMember, setTeamMember] = useState(null);
  const [error, setError] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);

  // Permission states
  const [selectedApps, setSelectedApps] = useState([]);
  const [appPermissions, setAppPermissions] = useState({});
  const [aiUsageLimit, setAiUsageLimit] = useState(1000);
  const [financialAccess, setFinancialAccess] = useState(true);
  const [showFinancialToggle, setShowFinancialToggle] = useState(false);

  // Available apps with colors and abbreviations
  const ALL_APPS = [
    { id: 'workflows', name: 'Workflows', abbr: 'Wf', color: '#FF3B30', description: 'Workflow automation' },
    { id: 'bookings', name: 'Bookings', abbr: 'Boo', color: '#FFFF00', description: 'Manage bookings and shows' },
    { id: 'catalog', name: 'Catalog', abbr: 'Ca', color: '#17A2B8', description: 'Manage music catalog' },
    { id: 'tourmngr', name: 'TourMngr', abbr: 'TM', color: '#FF9500', description: 'Tour management' },
    { id: 'notes', name: 'Notes', abbr: 'Not', color: '#7C4DFF', description: 'Notes and documentation' },
    { id: 'connectors', name: 'Connectors', abbr: 'Cnr', color: '#4285F4', description: 'Third-party integrations' },
    { id: 'analytics', name: 'Analytics', abbr: 'An', color: '#34C759', description: 'View performance data' },
    { id: 'tasks', name: 'Tasks', abbr: 'Tsk', color: '#FF6B35', description: 'Task management' },
    { id: 'knowledge', name: 'Knowledge', abbr: 'Kw', color: '#5856D6', description: 'Knowledge base' },
    { id: 'contacts', name: 'Contacts', abbr: 'Cnt', color: '#B08A4C', description: 'Contact management' },
    { id: 'ai', name: 'AI', abbr: 'AI', color: '#6366F1', description: 'AI assistant and automation' }
  ];

  useEffect(() => {
    fetchTeamMember();
  }, [teamMemberId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdown && !event.target.closest('.app-dropdown')) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdown]);

  const fetchTeamMember = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('dizeeToken');
      const response = await getDataAPI(`team/member/${teamMemberId}`, token);
      
      if (response.data?.status === 200) {
        const memberData = response.data.data;
        setTeamMember(memberData);
        
        // Set current permissions
        const permissions = memberData.permissions || {};
        setSelectedApps(permissions.apps || []);
        setAppPermissions(permissions.appPermissions || {});
        setAiUsageLimit(permissions.aiUsageLimit || 1000);
        
        // Set financial access (only for Manager accounts)
        const accountType = memberData.role || memberData.accountType;
        setShowFinancialToggle(accountType === 'manager');
        setFinancialAccess(memberData.financialAccess !== false);
      } else {
        setError(response.data?.message || 'Failed to load team member');
      }
    } catch (error) {
      console.error('Error fetching team member:', error);
      setError(error.response?.data?.message || 'Failed to load team member');
    } finally {
      setLoading(false);
    }
  };

  const handleAppToggle = (appId) => {
    setSelectedApps(prev => {
      const newSelection = prev.includes(appId) 
        ? prev.filter(id => id !== appId)
        : [...prev, appId];
      
      // Remove permissions for unselected apps
      if (!newSelection.includes(appId)) {
        setAppPermissions(prevPerms => {
          const newPerms = { ...prevPerms };
          delete newPerms[appId];
          return newPerms;
        });
      } else {
        // Add default permission for newly selected apps
        setAppPermissions(prevPerms => ({
          ...prevPerms,
          [appId]: appId === 'analytics' ? 'view' : 'edit'
        }));
      }
      
      return newSelection;
    });
  };

  const handleAppPermissionChange = (appId, permission) => {
    // Force Support accounts to only have 'view' permissions
    const finalPermission = teamMember?.role === 'support' && permission === 'edit' 
      ? 'view' 
      : permission;
      
    setAppPermissions(prev => ({
      ...prev,
      [appId]: finalPermission
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      const token = localStorage.getItem('dizeeToken');
      const permissionsData = {
        teamMemberId,
        permissions: {
          accessLevel: teamMember?.role === 'support' ? 'view' : 'edit',
          apps: selectedApps,
          appPermissions,
          aiUsageLimit: parseInt(aiUsageLimit)
        }
      };

      const response = await putDataAPI('team/member/permissions', permissionsData, token);
      
      // Also update financial access if this is a Manager account
      if (showFinancialToggle && response.data?.status === 200) {
        await putDataAPI('team/member/financial-access', {
          teamMemberId,
          financialAccess
        }, token);
      }
      
      if (response.data?.status === 200) {
        dispatch(setSuccessAlert('Team member permissions updated successfully'));
        navigate('/team-management');
      } else {
        throw new Error('Failed to update permissions');
      }
    } catch (error) {
      console.error('Error updating permissions:', error);
      dispatch(setErrorAlert('Failed to update team member permissions'));
    } finally {
      setSaving(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return 'TM';
    return name.split(' ').map(word => word[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <LayoutHeader>
      <div className="flex justify-center w-full">
        <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">
          {/* Header */}
          <div className="px-4 my-[50px] flex w-full justify-between items-center">
            <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Edit Team Member</span>
            <div className="flex items-center gap-4">
              <button 
                onClick={handleSave} 
                disabled={saving}
                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
              <span 
                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
                onClick={() => navigate('/team-management')}
              >
                Go back
              </span>
            </div>
          </div>

          {/* Content */}
          <div className="w-full px-4 flex-1 overflow-y-auto">
            {loading ? (
              <div className="text-[rgba(255,255,255,0.5)] text-center py-8 text-[12px]">
                Loading team member...
              </div>
            ) : error ? (
              <div className="text-red-400 text-center py-8 text-[12px]">{error}</div>
            ) : (
              <>
                {/* Team Member Info Card */}
                <div className="mb-6 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-[14px]"
                      style={{ background: `linear-gradient(45deg, ${appColor}, #a78bfa)` }}
                    >
                      {teamMember?.profilePicture ? (
                        <img 
                          src={teamMember.profilePicture} 
                          alt={teamMember.name}
                          className="w-full h-full rounded-full object-cover"
                        />
                      ) : (
                        getInitials(teamMember?.name || teamMember?.email)
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="text-white text-[14px] font-medium">
                        {teamMember?.name || teamMember?.email}
                      </p>
                      {teamMember?.name && teamMember?.name !== teamMember?.email && (
                        <p className="text-[rgba(255,255,255,0.6)] text-[11px]">
                          {teamMember?.email}
                        </p>
                      )}
                      <div 
                        className="inline-block mt-1 px-2 py-0.5 rounded text-[10px] font-medium"
                        style={{
                          backgroundColor: `${appColor}20`,
                          color: appColor
                        }}
                      >
                        {teamMember?.role === 'manager' ? 'Manager' : teamMember?.role === 'support' ? 'Support' : teamMember?.role}
                      </div>
                    </div>
                  </div>
                </div>

                {/* App Permissions */}
                <div className="mb-6">
                  <h3 className="text-white text-[12px] font-semibold mb-3">App Permissions</h3>
                  <div className="space-y-2">
                    {ALL_APPS.map((app) => {
                      // Hide AI completely for support accounts
                      if (app.id === 'ai' && teamMember?.role === 'support') {
                        return null;
                      }
                      
                      const isVisible = selectedApps.includes(app.id);
                      const permissionLevel = teamMember?.role === 'support' 
                        ? 'view' 
                        : (appPermissions[app.id] || 'view');
                      
                      return (
                        <div 
                          key={app.id} 
                          className="flex items-center justify-between bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-3 transition-all"
                          onMouseEnter={(e) => e.currentTarget.style.borderColor = appColor}
                          onMouseLeave={(e) => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'}
                        >
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-8 h-8 rounded flex items-center justify-center text-white text-[10px] font-semibold"
                              style={{ backgroundColor: app.color }}
                            >
                              {app.abbr}
                            </div>
                            <span className="text-white text-[13px]">{app.name}</span>
                          </div>
                          
                          <div className="relative app-dropdown">
                            <button 
                              onClick={() => setOpenDropdown(openDropdown === app.id ? null : app.id)}
                              className={`px-3 py-1 rounded text-[11px] font-medium transition-all ${
                                !isVisible ? 'bg-[rgba(255,255,255,0.1)] text-[rgba(255,255,255,0.4)]' : 
                                permissionLevel === 'edit' ? 'bg-[rgba(255,107,53,0.2)] text-[#FF6B35]' : 
                                'bg-[rgba(59,130,246,0.2)] text-[#3B82F6]'
                              }`}
                            >
                              {!isVisible ? 'Hide' : permissionLevel === 'edit' ? 'Edit' : 'View'}
                            </button>
                            
                            {/* Dropdown menu */}
                            {openDropdown === app.id && (
                              <div className="absolute right-0 top-full mt-1 bg-[#111111] rounded-lg border border-[rgba(255,255,255,0.1)] shadow-lg z-50 min-w-[100px] overflow-hidden">
                                {/* For AI app, only show Edit and Hide (no View option) */}
                                {app.id === 'ai' ? (
                                  <>
                                    {teamMember?.role !== 'support' && (
                                      <button
                                        onClick={() => {
                                          if (!isVisible) {
                                            setSelectedApps(prev => [...prev, app.id]);
                                            setAppPermissions(prev => ({ ...prev, [app.id]: 'edit' }));
                                          } else {
                                            handleAppPermissionChange(app.id, 'edit');
                                          }
                                          setOpenDropdown(null);
                                        }}
                                        className="w-full px-3 py-2 text-[12px] text-left text-[#FF6B35] hover:bg-[rgba(255,255,255,0.05)] transition-colors"
                                      >
                                        Edit
                                      </button>
                                    )}
                                    <button
                                      onClick={() => {
                                        if (isVisible) {
                                          handleAppToggle(app.id);
                                        }
                                        setOpenDropdown(null);
                                      }}
                                      className="w-full px-3 py-2 text-[12px] text-left text-[rgba(255,255,255,0.4)] hover:bg-[rgba(255,255,255,0.05)] transition-colors"
                                    >
                                      Hide
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    {/* For all other apps, show Edit, View, and Hide */}
                                    {teamMember?.role !== 'support' && (
                                      <button
                                        onClick={() => {
                                          if (!isVisible) {
                                            setSelectedApps(prev => [...prev, app.id]);
                                            setAppPermissions(prev => ({ ...prev, [app.id]: 'edit' }));
                                          } else {
                                            handleAppPermissionChange(app.id, 'edit');
                                          }
                                          setOpenDropdown(null);
                                        }}
                                        className="w-full px-3 py-2 text-[12px] text-left text-[#FF6B35] hover:bg-[rgba(255,255,255,0.05)] transition-colors"
                                      >
                                        Edit
                                      </button>
                                    )}
                                    <button
                                      onClick={() => {
                                        if (!isVisible) {
                                          setSelectedApps(prev => [...prev, app.id]);
                                          setAppPermissions(prev => ({ ...prev, [app.id]: 'view' }));
                                        } else {
                                          handleAppPermissionChange(app.id, 'view');
                                        }
                                        setOpenDropdown(null);
                                      }}
                                      className="w-full px-3 py-2 text-[12px] text-left text-[#3B82F6] hover:bg-[rgba(255,255,255,0.05)] transition-colors"
                                    >
                                      View
                                    </button>
                                    <button
                                      onClick={() => {
                                        if (isVisible) {
                                          handleAppToggle(app.id);
                                        }
                                        setOpenDropdown(null);
                                      }}
                                      className="w-full px-3 py-2 text-[12px] text-left text-[rgba(255,255,255,0.4)] hover:bg-[rgba(255,255,255,0.05)] transition-colors"
                                    >
                                      Hide
                                    </button>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Financial Access Toggle (Manager only) */}
                {showFinancialToggle && (
                  <div className="mb-6 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <p className="text-white text-[12px] font-semibold mb-1">Financial Access</p>
                        <p className="text-[rgba(255,255,255,0.6)] text-[10px] leading-[1.4]">
                          Allow access to financial data in Bookings, TourMngr, and Analytics
                        </p>
                      </div>
                      <button
                        onClick={() => setFinancialAccess(!financialAccess)}
                        className={`ml-3 w-11 h-6 rounded-full transition-all flex-shrink-0 ${
                          financialAccess ? 'bg-[#34C759]' : 'bg-[rgba(255,255,255,0.2)]'
                        }`}
                      >
                        <div className={`w-5 h-5 rounded-full bg-white transition-transform ${
                          financialAccess ? 'translate-x-[22px]' : 'translate-x-[2px]'
                        }`} style={{ marginTop: '2px' }}></div>
                      </button>
                    </div>
                  </div>
                )}

                {/* AI Usage Limit */}
                <div className="mb-6">
                  <h3 className="text-white text-[12px] font-semibold mb-2">AI Usage Limit (monthly)</h3>
                  <input
                    type="number"
                    value={aiUsageLimit}
                    onChange={(e) => setAiUsageLimit(e.target.value)}
                    placeholder="Enter monthly AI query limit"
                    className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 text-white text-[12px] placeholder-[rgba(255,255,255,0.5)] focus:outline-none transition-colors"
                    style={{
                      borderColor: 'rgba(255,255,255,0.1)',
                    }}
                    onFocus={(e) => e.target.style.borderColor = appColor}
                    onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
                    min="0"
                  />
                  <p className="text-[rgba(255,255,255,0.6)] text-[10px] mt-2">
                    Set to 0 for unlimited usage
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </LayoutHeader>
  );
};

export default TeamMemberEdit;

